/**
 * «مواعيد المقابلات» (owner request 2026-11-01): the administration opens interview times on chosen days,
 * every few minutes within a period, and sees the next two weeks: free times (removable) and who booked.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, X } from "lucide-react";
import type { CreateInterviewSlotsResponse, InterviewSlotsResponse } from "@mubin/contracts";
import { Empty, ErrorNote, Field, Loading, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { ar, clock, dayKey, hijriDayNum, hijriMonth, weekday } from "../lib/interview";
import { useOrg } from "../lib/org";

const TZ = "Asia/Riyadh";
const DAYS = 14;

export function InterviewSlotsPage() {
  const org = useOrg();
  const qc = useQueryClient();
  const days = useMemo(
    () =>
      Array.from({ length: DAYS }, (_, i) => {
        const at = new Date(Date.now() + i * 86_400_000);
        return { key: dayKey(at, TZ), iso: at.toISOString() };
      }),
    [],
  );
  const from = days[0]!.key;
  const to = days[DAYS - 1]!.key;
  const list = useQuery({
    queryKey: ["interview-slots", org?.id, from, to],
    queryFn: () =>
      api<InterviewSlotsResponse>(`/v1/interview-slots?organizationId=${org!.id}&from=${from}&to=${to}`),
    enabled: !!org,
  });
  const tz = list.data?.timezone ?? TZ;

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [start, setStart] = useState("19:00");
  const [end, setEnd] = useState("21:00");
  const [minutes, setMinutes] = useState(20);
  const [capacity, setCapacity] = useState(1);
  const [done, setDone] = useState<string | null>(null);
  const { busy, error, run } = useSubmit();
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const perDay = Math.max(0, Math.floor((toMin(end) - toMin(start)) / minutes));
  const total = perDay * picked.size * capacity;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    setDone(null);
    void run(async () => {
      const r = await send<CreateInterviewSlotsResponse>("POST", "/v1/interview-slots", {
        organizationId: org.id,
        days: [...picked].sort(),
        from: start,
        to: end,
        minutes,
        capacity,
      });
      setDone(
        r.created
          ? `أُضيف ${ar(r.created)} موعدًا${r.skipped ? `، وتُرك ${ar(r.skipped)} موجودًا أو فائتًا` : ""}`
          : "لم يُضف شيء: الأوقات موجودة مسبقًا أو فاتت",
      );
      setPicked(new Set());
      await qc.invalidateQueries({ queryKey: ["interview-slots"] });
    }, errorText);
  }

  const [removing, setRemoving] = useState<string | null>(null);
  async function remove(id: string) {
    setRemoving(id);
    try {
      await api(`/v1/interview-slots/${id}`, { method: "DELETE" });
      await qc.invalidateQueries({ queryKey: ["interview-slots"] });
    } finally {
      setRemoving(null);
    }
  }

  return (
    <>
      <PageTitle
        title="مواعيد المقابلات"
        sub="حدّد الأوقات المتاحة، ويختار منها المتقدمون عبر روابطهم الخاصة"
      />
      <div className="ivs-grid">
        <form className="card form ivs-card" onSubmit={submit}>
          <ErrorNote error={error} />
          <Field id="ivs-days" label="الأيام">
            <div className="ivs-chips" id="ivs-days" role="group">
              {days.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  className="ivs-chip"
                  aria-pressed={picked.has(d.key)}
                  onClick={() =>
                    setPicked((p) => {
                      const n = new Set(p);
                      if (n.has(d.key)) n.delete(d.key);
                      else n.add(d.key);
                      return n;
                    })
                  }
                >
                  {weekday(d.iso, tz)} {hijriDayNum(d.iso, tz)}
                </button>
              ))}
            </div>
          </Field>
          <div className="row-actions" style={{ gap: 10 }}>
            <Field id="ivs-from" label="من الساعة">
              <input id="ivs-from" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field id="ivs-to" label="إلى الساعة">
              <input id="ivs-to" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
            </Field>
          </div>
          <Field id="ivs-min" label="كل موعد" hint="الفرق بين الموعد والذي بعده">
            <div className="ivs-chips" id="ivs-min" role="group">
              {[15, 20, 30, 45].map((m) => (
                <button
                  key={m}
                  type="button"
                  className="ivs-chip"
                  aria-pressed={minutes === m}
                  onClick={() => setMinutes(m)}
                >
                  {ar(m)} دقيقة
                </button>
              ))}
            </div>
          </Field>
          <Field id="ivs-cap" label="مقابلات في نفس الوقت" hint="بعدد من يقابل من الإدارة">
            <input
              id="ivs-cap"
              type="number"
              min={1}
              max={10}
              value={capacity}
              onChange={(e) => setCapacity(Math.min(10, Math.max(1, Number(e.target.value) || 1)))}
              style={{ width: 120 }}
            />
          </Field>
          <p className="ivs-preview">
            {picked.size === 0
              ? "اختر يومًا أو أكثر"
              : `سيُضاف ${ar(perDay)} موعدًا في كل يوم${capacity > 1 ? ` × ${ar(capacity)} مقابلات` : ""} = ${ar(total)}`}
          </p>
          {done && (
            <p className="small" role="status">
              {done}
            </p>
          )}
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy || picked.size === 0 || perDay === 0}>
              <CalendarPlus size={18} strokeWidth={1.75} /> {busy ? "جارٍ الإضافة…" : "إضافة الأوقات"}
            </button>
          </div>
        </form>
        <section className="card ivs-card">
          <h3 style={{ marginBottom: 6 }}>الأسبوعان القادمان</h3>
          {list.isPending ? (
            <Loading />
          ) : list.isError ? (
            <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
          ) : (
            <>
              {days.map((d) => {
                const slots = list.data.slots.filter((s) => dayKey(s.startsAt, tz) === d.key);
                return (
                  <div className="ivs-day" key={d.key}>
                    <div className="ivs-dn">
                      <b>{weekday(d.iso, tz)}</b>
                      <span>
                        {hijriDayNum(d.iso, tz)} {hijriMonth(d.iso, tz)}
                      </span>
                    </div>
                    <div className="ivs-slots">
                      {slots.length === 0 && <span className="muted small">لا مواعيد</span>}
                      {slots.map((s) =>
                        s.booked.length ? (
                          <span key={s.id} className="ivs-slot booked" title="محجوز">
                            {clock(s.startsAt, tz)}
                            <small>· {s.booked.map((b) => b.name.split(/\s+/)[0]).join("، ")}</small>
                            {s.capacity > s.booked.length && (
                              <small>({ar(s.capacity - s.booked.length)} متاح)</small>
                            )}
                          </span>
                        ) : (
                          <button
                            key={s.id}
                            type="button"
                            className="ivs-slot"
                            title="حذف هذا الوقت"
                            aria-label={`حذف ${clock(s.startsAt, tz)}`}
                            disabled={removing === s.id}
                            onClick={() => void remove(s.id)}
                          >
                            {clock(s.startsAt, tz)}
                            {s.capacity > 1 && <small>×{ar(s.capacity)}</small>}
                            <X size={12} />
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="muted small" style={{ marginTop: 10 }}>
                🟩 متاح (اضغط لحذفه) · 🟨 محجوز
              </p>
            </>
          )}
        </section>
      </div>
    </>
  );
}
